import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'tr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  tr: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.allTasks': 'All Tasks',
    'nav.trackedTasks': 'Tracked Tasks',
    'nav.walletAnalysis': 'Wallet Analysis',
    'nav.leaderboard': 'Leaderboard',
    'nav.analytics': 'Analytics',
    'nav.discover': 'Discover',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.refresh': 'Refresh',
    'common.connectWallet': 'Connect Wallet',
    'common.disconnectWallet': 'Disconnect Wallet',
    
    // Dashboard
    'dashboard.welcome': 'Welcome',
    'dashboard.dailyGoal': 'Daily Goal',
    'dashboard.weeklyProgress': 'Weekly Progress',
    'dashboard.totalRewards': 'Total Rewards',
    'dashboard.featuredProjects': 'Featured Projects',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.quickStats': 'Quick Stats',
    
    // Tasks
    'tasks.title': 'Tasks',
    'tasks.all': 'All Tasks',
    'tasks.active': 'Active',
    'tasks.completed': 'Completed',
    'tasks.difficulty': 'Difficulty',
    'tasks.category': 'Category',
    'tasks.reward': 'Reward',
    'tasks.timeEstimate': 'Time Estimate',
    'tasks.requirements': 'Requirements',
    'tasks.description': 'Description',
    'tasks.blockchain': 'Blockchain',
    'tasks.airdropPotential': 'Airdrop Potential',
    'tasks.status': 'Status',
    'tasks.participants': 'Participants',
    'tasks.startTask': 'Start Task',
    'tasks.trackTask': 'Track Task',
    'tasks.untrackTask': 'Untrack Task',
    'tasks.viewDetails': 'View Details',
    'tasks.noTasks': 'No tasks found',
    'tasks.searchPlaceholder': 'Search tasks...',
    
    // Wallet
    'wallet.title': 'Wallet Analysis',
    'wallet.connect': 'Connect Wallet',
    'wallet.balance': 'Balance',
    'wallet.transactions': 'Transactions',
    'wallet.nfts': 'NFTs',
    'wallet.defi': 'DeFi Positions',
    'wallet.analytics': 'Analytics',
    'wallet.portfolio': 'Portfolio',
    'wallet.history': 'History',
    'wallet.rewards': 'Rewards',
    'wallet.noTransactions': 'No transactions found',
    'wallet.noTransactionsDesc': 'No recent transactions found on this chain',
    
    // Leaderboard
    'leaderboard.title': 'Leaderboard',
    'leaderboard.rank': 'Rank',
    'leaderboard.user': 'User',
    'leaderboard.level': 'Level',
    'leaderboard.xp': 'XP',
    'leaderboard.completedTasks': 'Completed Tasks',
    
    // Analytics
    'analytics.title': 'Analytics',
    'analytics.overview': 'Overview',
    'analytics.performance': 'Performance',
    'analytics.trends': 'Trends',
    'analytics.insights': 'Insights',
    'analytics.reports': 'Reports',
    'analytics.export': 'Export',
    'analytics.timeRange': 'Time Range',
    'analytics.daily': 'Daily',
    'analytics.weekly': 'Weekly',
    'analytics.monthly': 'Monthly',
    'analytics.yearly': 'Yearly'
  },
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.allTasks': 'All Tasks',
    'nav.trackedTasks': 'Tracked Tasks',
    'nav.walletAnalysis': 'Wallet Analysis',
    'nav.leaderboard': 'Leaderboard',
    'nav.analytics': 'Analytics',
    'nav.discover': 'Discover',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.view': 'View',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.refresh': 'Refresh',
    'common.connectWallet': 'Connect Wallet',
    'common.disconnectWallet': 'Disconnect Wallet',
    
    // Dashboard
    'dashboard.welcome': 'Welcome',
    'dashboard.dailyGoal': 'Daily Goal',
    'dashboard.weeklyProgress': 'Weekly Progress',
    'dashboard.totalRewards': 'Total Rewards',
    'dashboard.featuredProjects': 'Featured Projects',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.quickStats': 'Quick Stats',
    
    // Tasks
    'tasks.title': 'Tasks',
    'tasks.all': 'All Tasks',
    'tasks.active': 'Active',
    'tasks.completed': 'Completed',
    'tasks.difficulty': 'Difficulty',
    'tasks.category': 'Category',
    'tasks.reward': 'Reward',
    'tasks.timeEstimate': 'Time Estimate',
    'tasks.requirements': 'Requirements',
    'tasks.description': 'Description',
    'tasks.blockchain': 'Blockchain',
    'tasks.airdropPotential': 'Airdrop Potential',
    'tasks.status': 'Status',
    'tasks.participants': 'Participants',
    'tasks.startTask': 'Start Task',
    'tasks.trackTask': 'Track Task',
    'tasks.untrackTask': 'Untrack Task',
    'tasks.viewDetails': 'View Details',
    'tasks.noTasks': 'No tasks found',
    'tasks.searchPlaceholder': 'Search tasks...',
    
    // Wallet
    'wallet.title': 'Wallet Analysis',
    'wallet.connect': 'Connect Wallet',
    'wallet.balance': 'Balance',
    'wallet.transactions': 'Transactions',
    'wallet.nfts': 'NFTs',
    'wallet.defi': 'DeFi Positions',
    'wallet.analytics': 'Analytics',
    'wallet.portfolio': 'Portfolio',
    'wallet.history': 'History',
    'wallet.rewards': 'Rewards',
    'wallet.noTransactions': 'No transactions found',
    'wallet.noTransactionsDesc': 'No recent transactions found on this chain',
    
    // Leaderboard
    'leaderboard.title': 'Leaderboard',
    'leaderboard.rank': 'Rank',
    'leaderboard.user': 'User',
    'leaderboard.level': 'Level',
    'leaderboard.xp': 'XP',
    'leaderboard.completedTasks': 'Completed Tasks',
    
    // Analytics
    'analytics.title': 'Analytics',
    'analytics.overview': 'Overview',
    'analytics.performance': 'Performance',
    'analytics.trends': 'Trends',
    'analytics.insights': 'Insights',
    'analytics.reports': 'Reports',
    'analytics.export': 'Export',
    'analytics.timeRange': 'Time Range',
    'analytics.daily': 'Daily',
    'analytics.weekly': 'Weekly',
    'analytics.monthly': 'Monthly',
    'analytics.yearly': 'Yearly'
  }
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('en'); // Default to English

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'tr' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
  }, []);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    try {
      const translation = translations[language]?.[key as keyof typeof translations['en']];
      return translation || key; // Fallback to key if translation not found
    } catch (error) {
      console.warn(`Translation error for key: ${key}`, error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Fallback translation function
    const fallbackT = (key: string) => key;
    return { language: 'en' as Language, setLanguage: () => {}, t: fallbackT };
  }
  return context;
} 